import { getSupabaseClient as getUnifiedSupabaseClient } from '../lib/supabase';

// Use the unified Supabase client
function getSupabaseClient() {
  return getUnifiedSupabaseClient();
}

export interface Note {
  id: string;
  seeker_id: string | null;
  guide_id: string | null;
  note_content: string;
  note_leaver_id: string;
  created_at: string;
  updated_at: string;
  leaver_email?: string;
  leaver_name?: string;
  leaver_user_type?: string;
  subject_name?: string;
  subject_type?: 'seeker' | 'guide';
}

export interface CreateNoteRequest {
  seeker_id?: string;
  guide_id?: string;
  note_content: string;
}

export class NotesService {
  /**
   * Get all notes for a specific guide
   */
  static async getGuideNotes(guideId: string): Promise<Note[]> {
    const supabase = getSupabaseClient();
    
    const { data, error } = await supabase
      .from('notes_with_users')
      .select('*')
      .eq('guide_id', guideId)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch guide notes: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Get all notes for a specific seeker
   */
  static async getSeekerNotes(seekerId: string): Promise<Note[]> {
    const supabase = getSupabaseClient();
    
    const { data, error } = await supabase
      .from('notes_with_users')
      .select('*')
      .eq('seeker_id', seekerId)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch seeker notes: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Create a new note on a guide
   */
  static async createGuideNote(guideId: string, noteContent: string): Promise<Note> {
    const supabase = getSupabaseClient();
    
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('You must be logged in to create notes');
    }
    
    const { data, error } = await supabase
      .from('notes')
      .insert({
        guide_id: guideId,
        note_content: noteContent,
        note_leaver_id: user.id
      } as any)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create guide note: ${error.message}`);
    }

    return data;
  }

  /**
   * Create a new note on a seeker
   */
  static async createSeekerNote(seekerId: string, noteContent: string): Promise<Note> {
    const supabase = getSupabaseClient();
    
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('You must be logged in to create notes');
    }
    
    const { data, error } = await supabase
      .from('notes')
      .insert({
        seeker_id: seekerId,
        note_content: noteContent,
        note_leaver_id: user.id
      } as any)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create seeker note: ${error.message}`);
    }

    return data;
  }

  /**
   * Update an existing note
   */
  static async updateNote(noteId: string, noteContent: string): Promise<Note> {
    const supabase = getSupabaseClient();
    
    const { data, error } = await (supabase as any)
      .from('notes')
      .update({ note_content: noteContent })
      .eq('id', noteId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update note: ${error.message}`);
    }

    return data;
  }

  /**
   * Delete a note
   */
  static async deleteNote(noteId: string): Promise<void> {
    const supabase = getSupabaseClient();
    
    const { error } = await supabase
      .from('notes')
      .delete()
      .eq('id', noteId);

    if (error) {
      throw new Error(`Failed to delete note: ${error.message}`);
    }
  }

  /**
   * Get all notes (admin only)
   */
  static async getAllNotes(): Promise<Note[]> {
    const supabase = getSupabaseClient();
    
    const { data, error } = await supabase
      .from('notes_with_users')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch all notes: ${error.message}`);
    }

    return data || [];
  }
}